<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Popover, Text, Button } from '@Shelly/shelly-design-system';

	export let step: number = 0;
	export let show: boolean = false;
	export let portalTarget: string = '.modal-panel';
	let className: string = '';
	export { className as class };

	const dispatch = createEventDispatcher<{ next: void; skip: void }>();

	const STEPS = [
		{
			titleKey: 'ai_chat_popover_step1_title',
			titleDefault: 'Welcome to Shelly AI Assistant',
			textKey: 'ai_chat_popover_step1_text',
			textDefault: 'You have an allocation for how many prompts you can send per day',
			showSkip: true,
			placement: 'top' as const,
		},
		{
			titleKey: 'ai_chat_popover_step2_title',
			titleDefault: 'Shelly Assistant',
			textKey: 'ai_chat_popover_step2_text',
			textDefault: 'Choose a preset from the smart suggestions or write your own prompt',
			showSkip: true,
			placement: 'bottom' as const,
		},
		{
			titleKey: 'ai_chat_popover_step3_title',
			titleDefault: 'Shelly Assistant',
			textKey: 'ai_chat_popover_step3_text',
			textDefault: 'Add rooms, zones, devices or scenes to your query.',
			showSkip: true,
			placement: 'top' as const,
		},
		{
			titleKey: 'ai_chat_popover_step4_title',
			titleDefault: 'Shelly Assistant',
			textKey: 'ai_chat_popover_step4_text',
			textDefault: 'Choose one of our expert models',
			showSkip: false,
			placement: 'bottom' as const,
		},
	];

	$: currentStep = STEPS[step] || STEPS[0];
	$: isLastStep = step === 3;
	$: placement = currentStep.placement;
	$: title = $format(currentStep.titleKey, { default: currentStep.titleDefault });
	$: popoverElementClass = `ai-onboarding-popover ai-onboarding-popover-step${step}`;
	$: preventOutsideClickElement =
		show && portalTarget
			? ((document.querySelector(portalTarget) as HTMLElement | null) ?? undefined)
			: undefined;

	function handleNext() {
		dispatch('next');
	}

	function handleSkip() {
		dispatch('skip');
	}
</script>

<Popover
	class={className}
	{show}
	{placement}
	{title}
	{portalTarget}
	{preventOutsideClickElement}
	hideCloseBtn
	{popoverElementClass}
>
	<slot />
	<svelte:fragment slot="customContent">
		<Text styleType="body-small">
			{$format(currentStep.textKey, { default: currentStep.textDefault })}
		</Text>
	</svelte:fragment>
	<svelte:fragment slot="footer">
		<div class="popover-actions">
			{#if currentStep.showSkip}
				<Button
					variant="secondary"
					size="s"
					on:click={(e) => {
						e.stopPropagation();
						handleSkip();
					}}
					aria-label={$format('ai_chat_popover_skip', { default: 'Skip' })}
				>
					{$format('ai_chat_popover_skip', { default: 'Skip' })}
				</Button>
			{/if}
			<Button
				variant="primary"
				size="s"
				on:click={(e) => {
					e.stopPropagation();
					handleNext();
				}}
				aria-label={isLastStep
					? $format('ai_chat_popover_done', { default: 'Done' })
					: $format('ai_chat_popover_next', { default: 'Next' })}
			>
				{#if isLastStep}
					{$format('ai_chat_popover_done', { default: 'Done' })}
				{:else}
					{$format('ai_chat_popover_next', { default: 'Next' })}
				{/if}
			</Button>
		</div>
	</svelte:fragment>
</Popover>

<style>
	.popover-actions {
		display: flex;
		gap: 8px;
		justify-content: flex-end;
		margin-top: 8px;
	}
</style>
